/**
 * NotificationManagement Repository
 * 알림 관리 관련 API 호출 담당
 */
import { API_BASE_URL } from '../../../config/api';
import {
  NotificationTemplate,
  NotificationCustomer,
  SendHistory,
} from '../models/NotificationManagement';

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export class NotificationManagementRepository {
  /**
   * 알림 템플릿 목록 조회
   */
  async getTemplates(): Promise<NotificationTemplate[]> {
    const response = await fetch(`${API_BASE_URL}/api/v1/notifications/templates`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * 고객 목록 조회 (알림 발송 대상)
   * 회원 API를 사용하여 알림 발송 대상 고객 목록 조회
   */
  async getCustomers(): Promise<NotificationCustomer[]> {
    const response = await fetch(`${API_BASE_URL}/v1/owner/members`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const members = await response.json();

    // 회원 데이터를 NotificationCustomer 형식으로 변환
    return members.map((member: any) => ({
      id: member.id || member.member_id,
      name: member.name || member.member_name,
      phone: member.phone || member.member_phone,
      membershipType: member.membership_type || '일반',
      lastVisit: member.last_visit || member.updated_at || new Date().toISOString(),
      isActive: member.is_active !== false && member.status === 'active'
    }));
  }

  /**
   * 발송 이력 조회
   */
  async getSendHistory(): Promise<SendHistory[]> {
    const response = await fetch(`${API_BASE_URL}/api/v1/notifications/history`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * 알림 발송
   */
  async sendNotification(data: {
    templateId: string;
    customerIds: string[];
    customMessage?: string;
  }): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/v1/notifications/send`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  /**
   * 템플릿 생성
   */
  async createTemplate(
    template: Omit<NotificationTemplate, 'id' | 'createdAt'>
  ): Promise<NotificationTemplate> {
    const response = await fetch(`${API_BASE_URL}/api/v1/notifications/templates`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(template),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * 템플릿 수정
   */
  async updateTemplate(
    id: string,
    template: Partial<NotificationTemplate>
  ): Promise<NotificationTemplate> {
    const response = await fetch(`${API_BASE_URL}/api/v1/notifications/templates/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(template),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * 템플릿 삭제
   */
  async deleteTemplate(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/api/v1/notifications/templates/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }
}

export const notificationManagementRepository = new NotificationManagementRepository();
